import React, { useMemo } from 'react';
import { Avatar, Skeleton } from '@mui/material';
import { SvgIconProps } from '@mui/material/SvgIcon';
import { Category } from '../types/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { categoriesApi } from '../api/api';

// Material UI Icons
import LocalDrinkIcon from '@mui/icons-material/LocalDrink';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import KitchenIcon from '@mui/icons-material/Kitchen';
import EcoIcon from '@mui/icons-material/Compost';
import FreeBreakfastIcon from '@mui/icons-material/FreeBreakfast';
import BakeryDiningIcon from '@mui/icons-material/BakeryDining';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import CategoryIcon from '@mui/icons-material/Category';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import IcecreamIcon from '@mui/icons-material/Icecream';
import SpaIcon from '@mui/icons-material/Spa';

export interface CategoryIconComponentProps {
  category?: Category;
  categoryId?: number;
  size?: number;
  iconSize?: 'small' | 'medium' | 'large' | 'inherit';
}

export const getCategoryIcon = (categoryName: string, iconSize: SvgIconProps['fontSize'] = 'large') => {
  const name = categoryName.toLowerCase();
  
  if (name.includes('beverage') || name.includes('drink') || name.includes('water') || name.includes('soda')) {
    return <LocalDrinkIcon fontSize={iconSize} />;
  } else if (name.includes('snack') || name.includes('chocolate') || name.includes('chips')) {
    return <RestaurantIcon fontSize={iconSize} />;
  } else if (name.includes('bulk') || name.includes('waste')) {
    return <EcoIcon fontSize={iconSize} />;
  } else if (name.includes('produce') || name.includes('fruit') || name.includes('vegetable')) {
    return <LocalFloristIcon fontSize={iconSize} />;
  } else if (name.includes('grocery') || name.includes('pantry')) {
    return <ShoppingBasketIcon fontSize={iconSize} />;
  } else if (name.includes('allergen') || name.includes('specialty')) {
    return <SpaIcon fontSize={iconSize} />;
  } else if (name.includes('coffee') || name.includes('tea')) {
    return <FreeBreakfastIcon fontSize={iconSize} />;
  } else if (name.includes('bakery') || name.includes('bread')) {
    return <BakeryDiningIcon fontSize={iconSize} />;
  } else if (name.includes('cleaning') || name.includes('supplies')) {
    return <CleaningServicesIcon fontSize={iconSize} />;
  } else if (name.includes('frozen') || name.includes('ice cream')) {
    return <IcecreamIcon fontSize={iconSize} />;
  } else if (name.includes('refrigerated')) {
    return <KitchenIcon fontSize={iconSize} />;
  } else {
    // Default icon
    return <CategoryIcon fontSize={iconSize} />;
  }
};

export const getCategoryColor = (categoryName: string) => {
  const name = categoryName.toLowerCase();
  
  if (name.includes('beverage') || name.includes('drink') || name.includes('water')) {
    return '#3498db'; // Blue
  } else if (name.includes('snack')) {
    return '#e67e22'; // Orange
  } else if (name.includes('bulk') || name.includes('waste') || name.includes('eco')) {
    return '#2ecc71'; // Green
  } else if (name.includes('produce') || name.includes('fruit') || name.includes('vegetable')) {
    return '#27ae60'; // Dark green
  } else if (name.includes('grocery') || name.includes('pantry')) {
    return '#f39c12'; // Yellow
  } else if (name.includes('allergen') || name.includes('specialty')) {
    return '#9b59b6'; // Purple
  } else if (name.includes('coffee') || name.includes('tea')) {
    return '#795548'; // Brown
  } else if (name.includes('bakery') || name.includes('bread')) {
    return '#d35400'; // Dark orange
  } else if (name.includes('cleaning') || name.includes('supplies')) {
    return '#00bcd4'; // Cyan
  } else if (name.includes('frozen') || name.includes('ice cream')) {
    return '#03a9f4'; // Light blue
  } else {
    // Default color
    return '#607d8b'; // Blue grey
  }
};

// Try to guess category color and icon from the category ID for immediate rendering
const getDefaultCategoryStyleById = (id: number) => {
  // Hard-code common category IDs to their colors and icons for instant rendering
  const knownCategories: Record<number, { color: string, icon: React.ReactNode }> = {
    23353464: { color: '#3498db', icon: <LocalDrinkIcon /> },  // Beverages
    23353466: { color: '#e67e22', icon: <RestaurantIcon /> },  // Snacks
    23353467: { color: '#2ecc71', icon: <EcoIcon /> },         // Eco-friendly
    23353468: { color: '#27ae60', icon: <LocalFloristIcon /> } // Produce
  };
  
  if (knownCategories[id]) {
    return knownCategories[id];
  }
  
  // Default
  return { color: '#607d8b', icon: <CategoryIcon /> };
};

const CategoryIconComponent: React.FC<CategoryIconComponentProps> = ({ 
  category,
  categoryId,
  size = 56,
  iconSize = 'large'
}) => {
  const queryClient = useQueryClient();
  
  // Check if we already have the categories data in the cache
  const cachedCategories = useMemo(() => {
    const categoriesData = queryClient.getQueryData(['categories']) as { items?: Category[] } | undefined;
    if (categoriesData?.items && categoryId) {
      const foundCategory = categoriesData.items.find(
        (cat: Category) => cat.id === categoryId
      );
      if (foundCategory) {
        return foundCategory;
      }
    }
    return null;
  }, [queryClient, categoryId]);
  
  // If categoryId is provided but not category and not in cache, fetch the category data
  const { data: categoryData, isLoading } = useQuery({
    queryKey: ['category', categoryId],
    queryFn: () => categoryId ? categoriesApi.getCategory(categoryId) : null,
    enabled: !!categoryId && !category && !cachedCategories,
    staleTime: 300000, // 5 minutes
  });
  
  // Use the provided category, cached category, or the fetched category
  const categoryToUse = category || cachedCategories || categoryData;
  
  // For immediate rendering while loading, use default styling based on ID
  const defaultStyle = categoryId ? getDefaultCategoryStyleById(categoryId) : { color: '#607d8b', icon: <CategoryIcon /> };
  
  if (!categoryToUse && !isLoading) {
    // Default avatar if no category is available
    return (
      <Avatar 
        sx={{ 
          bgcolor: defaultStyle.color,
          width: size,
          height: size,
        }}
      >
        {defaultStyle.icon}
      </Avatar>
    );
  }
  
  if (isLoading || !categoryToUse) {
    // Render with default styling while loading for smoother experience
    return (
      <Avatar 
        sx={{ 
          bgcolor: defaultStyle.color,
          width: size,
          height: size,
          opacity: 0.9,
        }}
      >
        {defaultStyle.icon}
      </Avatar>
    );
  }
  
  return (
    <Avatar 
      sx={{ 
        bgcolor: getCategoryColor(categoryToUse.name),
        width: size,
        height: size,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}
    >
      {getCategoryIcon(categoryToUse.name, iconSize)}
    </Avatar>
  );
};

export default CategoryIconComponent; 